/**
 * advance-research.ts — per-tick research drain (M12).
 *
 * Runs inside advanceTick AFTER advancePopulationAndEconomy (so any
 * research income that landed this tick is available to drain) and
 * BEFORE the per-planet / per-ship passes (so a tech completion this
 * tick is visible to downstream consumers — e.g. the combat pass
 * reading `resolveSlotWeapon` will pick up a just-completed weapon Mk).
 *
 * Per empire:
 *   1. Skip if no active research target.
 *   2. Look up the tech. If missing OR already in researchedTechs,
 *      defensively clear the slot and continue.
 *   3. Transfer RP from empire.resources.research to
 *      activeResearch.rpAccumulated, bounded by the remaining cost.
 *   4. On reaching researchCost, add the tech name to researchedTechs
 *      and clear the slot.
 *
 * `researchTimeTicks` from the tech data is NOT honored in v1 per the
 * M12 design lock — completion is cost-only. The field remains on the
 * type for future balance passes.
 */

import type { GameState } from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';
import { effectiveMaxHull } from './research-effects';

/** Advance per-empire research by exactly one tick. Mutates state. */
export function advanceResearch(state: GameState): void {
  // Deterministic iteration: empire ids ascending.
  const empireIds = [...state.empires.keys()].sort((a, b) => a - b);

  for (const empireId of empireIds) {
    const empire = state.empires.get(empireId)!;
    // v3M21.1 — synthetic neutral empire never researches; the
    // activeResearch === null check below already short-circuits, but
    // skipping early keeps the intent obvious.
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    if (empire.activeResearch === null) continue;

    const tech = state.techs.get(empire.activeResearch.techName);
    if (!tech) {
      // Data drift — shouldn't happen post-load. Defensive clear.
      empire.activeResearch = null;
      continue;
    }
    if (empire.researchedTechs.has(tech.techName)) {
      // Tech was added through some other path (future save-import).
      // Clear the now-pointless slot rather than re-researching.
      empire.activeResearch = null;
      continue;
    }

    // Transfer RP — bounded by both the empire's current RP bank and
    // the remaining cost. Negative RP (shouldn't happen) is clamped to 0.
    const remaining = tech.researchCost - empire.activeResearch.rpAccumulated;
    const available = Math.max(0, empire.resources.research);
    const transfer = Math.min(remaining, available);
    if (transfer > 0) {
      empire.activeResearch.rpAccumulated += transfer;
      empire.resources.research -= transfer;
    }

    // Complete — add to researched set, clear slot. researchCost can be
    // 0 (Mk 1 baseline techs); in that case we complete the first tick
    // the target is set regardless of RP balance.
    if (empire.activeResearch.rpAccumulated >= tech.researchCost) {
      // MFix7.2 — Hull-tech retro-fit. Snapshot every owned-ship's
      // effective max hull BEFORE the tech promotes; after promotion,
      // bump currentHull by exactly (newMax - oldMax) per the locked
      // semantics: "400/600 + Hull tech (+200 max) → 600/800". Skip
      // dead ships (don't resurrect via Hull research).
      const isHullTech = tech.category === 'Hull';
      const preMaxByShipId = new Map<number, number>();
      if (isHullTech) {
        for (const shipId of empire.shipIds) {
          const ship = state.ships.get(shipId);
          if (!ship || ship.currentHull <= 0) continue;
          const tmpl = state.shipTemplates.get(
            `${empire.raceId}:${ship.chassis}`,
          );
          if (!tmpl) continue;
          preMaxByShipId.set(
            ship.id,
            effectiveMaxHull(state, empire, tmpl, ship),
          );
        }
      }

      empire.researchedTechs.add(tech.techName);
      empire.activeResearch = null;

      if (isHullTech) {
        for (const [shipId, oldMax] of preMaxByShipId) {
          const ship = state.ships.get(shipId);
          if (!ship || ship.currentHull <= 0) continue;
          const tmpl = state.shipTemplates.get(
            `${empire.raceId}:${ship.chassis}`,
          );
          if (!tmpl) continue;
          const newMax = effectiveMaxHull(state, empire, tmpl, ship);
          const delta = newMax - oldMax;
          if (delta > 0) ship.currentHull += delta;
        }
      }
    }

    // MFix1.1 — belt-and-suspenders clamp. The Math.min(remaining,
    // available) guard above can't push research below zero on its
    // own, but pinning the bank to >= 0 here documents the design
    // invariant ("research never goes negative") at the place
    // where someone would look first.
    if (empire.resources.research < 0) empire.resources.research = 0;
  }
}
